import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, BellOff, Check, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin → Settings → Notifications panel.
 *
 * - WhatsApp toggle: global kill-switch for status + payment WhatsApp messages.
 * - SMS toggle (reserved for future use; currently inert).
 *
 * Honoured by backend at /api/admin/notifications/settings.
 * Authenticates via the admin session token (Bearer) stored in localStorage
 * after `/api/admin/login`.
 */
export const NotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedToast, setSavedToast] = useState(false);
  const [settings, setSettings] = useState({
    whatsapp_enabled: true,
    sms_enabled: true,
    whatsapp_configured: false,
  });

  // Admin auth flows via the httpOnly `admin_session` cookie set at login,
  // sent automatically by axios (withCredentials=true set in Admin.jsx).

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/admin/notifications/settings`);
        setSettings(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load notification settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggle = async (key) => {
    const next = !settings[key];
    setSaving(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/admin/notifications/settings`,
        { [key]: next },
      );
      setSettings((s) => ({ ...s, ...res.data }));
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 1800);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center font-mono text-sm text-gray-600" data-testid="notif-settings-loading">
        Loading settings…
      </div>
    );
  }

  return (
    <div className="border-2 border-black p-6 bg-white space-y-6" data-testid="notif-settings-panel">
      <div>
        <h2 className="text-2xl font-black font-mono uppercase">Notification Settings</h2>
        <p className="text-sm text-gray-600 mt-1">Global kill-switch for outgoing customer notifications.</p>
      </div>

      {error && (
        <div className="p-3 border-2 border-red-700 bg-red-50 text-red-800 text-sm font-mono flex items-center gap-2">
          <AlertCircle size={14} />{error}
        </div>
      )}

      {savedToast && (
        <div className="p-2 border-2 border-green-700 bg-green-50 text-green-800 text-xs font-mono flex items-center gap-2">
          <Check size={14} /> Settings saved
        </div>
      )}

      {/* WhatsApp */}
      <div className="flex items-start justify-between gap-4 p-4 border-2 border-black">
        <div>
          <div className="flex items-center gap-2 font-bold font-mono uppercase">
            {settings.whatsapp_enabled ? <Bell size={16} /> : <BellOff size={16} />}
            WhatsApp Notifications
          </div>
          <div className="text-xs text-gray-600 mt-1 max-w-md">
            Send WhatsApp updates to customers for every booking status change (Paid, Picked Up, In Transit, Out for Delivery, Delivered, etc.) plus payment confirmation.
          </div>
          {!settings.whatsapp_configured && (
            <div className="text-xs text-amber-700 font-mono mt-2 flex items-center gap-1">
              <AlertCircle size={12} /> WhatsApp not configured on server — toggle has no effect.
            </div>
          )}
        </div>
        <button
          data-testid="toggle-whatsapp-notifications"
          onClick={() => toggle('whatsapp_enabled')}
          disabled={saving || !settings.whatsapp_configured}
          className={`px-4 py-2 border-2 border-black font-mono font-bold text-xs uppercase whitespace-nowrap transition-colors ${
            settings.whatsapp_enabled
              ? 'bg-green-500 text-white hover:bg-green-600'
              : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {settings.whatsapp_enabled ? 'ENABLED' : 'DISABLED'}
        </button>
      </div>

      <div className="text-xs text-gray-500 font-mono pt-3 border-t border-gray-300">
        Last updated: {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : '—'}
      </div>
    </div>
  );
};

export default NotificationSettings;
